import { type ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import type { InvitationDto } from '../types';
import { roleFromApi } from '@/core/constants/roles';

interface InvitationColumnsOptions {
  onCancel: (inv: InvitationDto) => void;
  isCancelling: (id: string) => boolean;
  t: TFunction;
}

function DateCell({ value }: { value: string | null | undefined }) {
  const formatInstant = useInstantFormat();
  return <span className="text-sm text-muted-foreground">{formatInstant(value) || '—'}</span>;
}

export function getInvitationColumns(
  options: InvitationColumnsOptions,
): ColumnDef<InvitationDto, unknown>[] {
  const { onCancel, isCancelling, t } = options;

  return [
    {
      accessorKey: 'email',
      header: t('invitations.columns.email'),
      meta: { cellClassName: 'flex-[2] min-w-0' },
      cell: ({ row }) => (
        <span className="text-sm font-medium text-foreground">{row.original.email ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'role',
      header: t('invitations.columns.role'),
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground capitalize">
          {row.original.role ? t(`role.${roleFromApi(row.original.role)}`) : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: t('invitations.columns.sentAt'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => <DateCell value={row.original.createdAt} />,
    },
    {
      accessorKey: 'expiresAt',
      header: t('invitations.columns.expires'),
      meta: { cellClassName: 'flex-none w-32' },
      cell: ({ row }) => <DateCell value={row.original.expiresAt} />,
    },
    {
      id: 'actions',
      header: undefined,
      meta: { cellClassName: 'flex-none w-12 flex justify-end' },
      cell: ({ row }) => {
        const inv = row.original;
        if (!inv.id) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                disabled={isCancelling(inv.id)}
                onClick={() => onCancel(inv)}
                data-testid={`invitation-cancel-${inv.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('invitations.cancel')}</TooltipContent>
          </Tooltip>
        );
      },
    },
  ];
}
